import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, TextField, MenuItem, Skeleton, Pagination, InputAdornment
} from '@mui/material';
import { Search, AccessTime, CalendarToday, ArrowForward } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { blogAPI } from '../../services/api';

const FOREST = '#163300';
const GREEN  = '#9FE870';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '';

const BlogCard = ({ blog, featured = false }) => (
  <Card
    component={Link}
    to={`/blog/${blog.slug}`}
    sx={{
      display: 'flex',
      flexDirection: featured ? { xs: 'column', md: 'row' } : 'column',
      height: '100%',
      textDecoration: 'none',
      borderRadius: 3,
      border: '1px solid',
      borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.08)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: '0 16px 40px rgba(22,51,0,0.12)',
        borderColor: GREEN,
        '& .blog-arrow': { transform: 'translateX(4px)' },
      },
    }}
  >
    {blog.featuredImage && (
      <CardMedia
        component="img"
        image={blog.featuredImage}
        alt={blog.title}
        loading="lazy"
        sx={{
          width: featured ? { xs: '100%', md: '45%' } : '100%',
          height: featured ? { xs: 220, md: 'auto' } : 200,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    )}
    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        {blog.category && (
          <Chip label={blog.category.name} size="small"
            sx={{ bgcolor: 'rgba(159,232,112,0.15)', color: FOREST, fontWeight: 700, fontSize: '0.72rem' }} />
        )}
        {blog.isFeatured && (
          <Chip label="Featured" size="small"
            sx={{ bgcolor: FOREST, color: GREEN, fontWeight: 700, fontSize: '0.72rem' }} />
        )}
      </Box>

      <Typography variant={featured ? 'h5' : 'h6'}
        sx={{ fontWeight: 800, mb: 1, lineHeight: 1.35, color: 'text.primary',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {blog.title}
      </Typography>

      {blog.excerpt && (
        <Typography color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.65, mb: 2, flex: 1,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {blog.excerpt}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 2,
        borderTop: '1px solid', borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,51,0,0.06)' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{fmtDate(blog.publishDate)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{blog.readingTime} min read</Typography>
          </Box>
        </Box>
        <ArrowForward className="blog-arrow" sx={{ fontSize: 18, color: FOREST, transition: 'transform 0.2s' }} />
      </Box>
    </CardContent>
  </Card>
);

const BlogCardSkeleton = () => (
  <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Skeleton variant="rectangular" height={200} />
    <CardContent sx={{ p: 3 }}>
      <Skeleton width="40%" height={20} sx={{ mb: 1 }} />
      <Skeleton width="90%" height={28} sx={{ mb: 0.5 }} />
      <Skeleton width="70%" height={28} sx={{ mb: 2 }} />
      <Skeleton width="100%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton width="80%" height={16} />
    </CardContent>
  </Card>
);

const BlogsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch]   = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [sort, setSort]       = useState(searchParams.get('sort') || 'newest');
  const [page, setPage]       = useState(parseInt(searchParams.get('page') || '1'));
  const [debouncedQ, setDebouncedQ] = useState(search);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const p = {};
    if (debouncedQ) p.q = debouncedQ;
    if (category)   p.category = category;
    if (sort !== 'newest') p.sort = sort;
    if (page > 1)   p.page = page;
    
    // Check if new params differ from current URL params to avoid redundant replaceState
    const currentParams = Object.fromEntries(searchParams.entries());
    const isSame = Object.keys(p).length === Object.keys(currentParams).length &&
                   Object.keys(p).every(k => p[k].toString() === currentParams[k]);
                   
    if (!isSame) {
      setSearchParams(p, { replace: true });
    }
  }, [debouncedQ, category, sort, page]); // eslint-disable-line

  const { data: catData } = useQuery(['blogCategories'], () => blogAPI.getCategories().then(r => r.data.data), { staleTime: 300000 });
  const { data: featuredData } = useQuery(['blogFeatured'], () => blogAPI.getFeatured().then(r => r.data.data), { staleTime: 60000 });

  const { data, isLoading } = useQuery(
    ['blogs', debouncedQ, category, sort, page],
    () => (debouncedQ
      ? blogAPI.search({ q: debouncedQ, category, sort, page, limit: 12 })
      : blogAPI.getAll({ category, sort, page, limit: 12 })
    ).then(r => r.data),
    { keepPreviousData: true }
  );

  const blogs    = data?.data || [];
  const total    = data?.total || 0;
  const pages    = data?.pages || 1;
  const featured = featuredData?.[0];

  const siteUrl = window.location.hostname.includes('.pk')
    ? 'https://fluxerp.pk'
    : window.location.hostname.includes('.qa')
      ? 'https://fluxerp.qa'
      : 'https://fluxerp.co.uk';

  const seo = {
    title: 'Blog & Resources - ERP Insights, Finance Tips & Business Growth | FluxERP',
    description: 'Expert articles on ERP implementation, IFRS accounting, inventory management, payroll, and business growth strategies from the FluxERP team.',
    canonical: '/blog',
    ogType: 'website',
    keywords: 'ERP blog, accounting tips, IFRS, inventory management, payroll software, business growth, cloud ERP',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      ],
    },
  };

  return (
    <WebsiteLayout>
      <SEO {...seo} />

      <HeroSection chip="Insights & Resources" minHeight="340px" py={{ xs: 7, md: 10 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 34, md: 52 }, fontWeight: 800, mb: 2, color: FOREST }}>
            News & Insights
          </Typography>
          <Typography sx={{ opacity: 0.85, color: FOREST, fontSize: '1.05rem', lineHeight: 1.7 }}>
            ERP best practices, finance tips, and business growth strategies from our team of experts.
          </Typography>
        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>

        {/* Featured post */}
        {!debouncedQ && !category && page === 1 && featured && (
          <Box sx={{ mb: 7 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: FOREST, letterSpacing: 2, mb: 2, display: 'block' }}>
              Featured Article
            </Typography>
            <BlogCard blog={featured} featured />
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search articles…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            size="small"
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
          />
          <TextField select size="small" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            sx={{ minWidth: 160 }} label="Category">
            <MenuItem value="">All Categories</MenuItem>
            {(catData || []).map(c => <MenuItem key={c.id} value={c.slug}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
            sx={{ minWidth: 140 }} label="Sort by">
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
          </TextField>
          {(debouncedQ || category) && (
            <Button size="small" onClick={() => { setSearch(''); setCategory(''); setPage(1); }}
              sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Clear filters
            </Button>
          )}
        </Box>

        {/* Results count */}
        {!isLoading && (
          <Typography color="text.secondary" sx={{ mb: 3, fontSize: '0.875rem' }}>
            {total} article{total !== 1 ? 's' : ''}{debouncedQ ? ` for "${debouncedQ}"` : ''}
          </Typography>
        )}

        {/* Grid */}
        <Grid container spacing={3}>
          {isLoading
            ? [...Array(9)].map((_, i) => <Grid item xs={12} sm={6} md={4} key={i}><BlogCardSkeleton /></Grid>)
            : blogs.length === 0
              ? (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No articles found</Typography>
                    <Typography color="text.secondary">Try a different search term or category.</Typography>
                  </Box>
                </Grid>
              )
              : blogs.map(b => (
                <Grid item xs={12} sm={6} md={4} key={b.id}>
                  <BlogCard blog={b} />
                </Grid>
              ))
          }
        </Grid>

        {/* Pagination */}
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={pages} page={page}
              onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              sx={{
                '& .MuiPaginationItem-root': { fontWeight: 600 },
                '& .Mui-selected': { bgcolor: `${FOREST} !important`, color: GREEN },
              }}
            />
          </Box>
        )}

        {/* CTA Banner */}
        <Box sx={{ mt: 10, p: { xs: 4, md: 6 }, borderRadius: 4, bgcolor: FOREST, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: GREEN, mb: 1.5 }}>
            Ready to transform your operations?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 4, maxWidth: 500, mx: 'auto' }}>
            See how FluxERP can streamline your finance, inventory, and HR in one platform.
          </Typography>
          <Button component={Link} to="/demo" variant="contained"
            endIcon={<ArrowForward />}
            sx={{ px: 5, py: 1.5, fontWeight: 800, bgcolor: GREEN, color: FOREST,
              '&:hover': { bgcolor: '#b6f192' }, borderRadius: 2 }}>
            Book a Free Demo
          </Button>
        </Box>
      </Container>
    </WebsiteLayout>
  );
};

export default BlogsPage;
